import * as Yup from "yup";
import { useState } from "react";
import { Circle } from "../../components/Animation";
import View from "../../components/GeneratePdf/View";
import Text from "../../components/GeneratePdf/Text";
import CustomTablePDF from "../../components/GeneratePdf/CustomTablePDF";
import { useDialog } from "../../contexts/DialogContext";
import { Button } from "../../components/Buttons";
import FormDefault from "../../components/Forms/FormDefault";
import { Page, Document, PDFViewer, Image } from "@react-pdf/renderer";
import * as dayjs from "dayjs";
import { useAuth } from "../../contexts/AuthContext";
import { Request } from "../../hooks/Request";
import { requestAuth } from "../../components/services/RequestService";
dayjs.locale("es");

// Datos de prueba
const MOCK_DATA = {
  accountNumbers: [
    { accountNumber: "100001", name: "Juan", fatherLastName: "Perez" },
    { accountNumber: "100002", name: "Maria", fatherLastName: "Gomez" },
  ],
  procedureData: {
    codigoDeInterno: "PLAN-2024-",
    senores: "EMPRESA ABC S.R.L.",
    nit: "123456789",
    docEmbarque: "DOC-001",
    intAgencia: "AG-001",
    facturas: "F001, F002",
    bultos: "5",
    peso: "500 kg",
    proveedor: "PROVEEDOR XYZ",
    mercancia: "MERCANCIA VARIADA",
    valorFob: "1000.00",
    flete1y2: "150.00",
    seguro: "50.00",
    otrosGastos: "30.00",
    valorCif: "1230.00",
    tc: "6.85",
    valorCifBs: "8425.50",
    aduana: "ADUANA ORURO",
    modalidad: "IMPORTACION",
    items: "10",
    tipo: "CONSUMO",
    dim: "DIM-001",
    nroFactura: "FAC-001",
    fechaValidacion: dayjs().format("DD/MM/YYYY"),
    totalProformaSus: "1230.00",
    totalProformaBs: "8425.50",
    otherChargesPaidByTheImporter: "200.00",
    totalAdvancePayments: "500.00",
    balanceOfPayroll: "150.00",
    clarifications: "DOCUMENTACIÓN COMPLETA",
  },
  documentItems: [
    { name: "Factura Comercial", originals: 1, copies: 2, legalized: 0 },
    { name: "Lista de Empaque", originals: 1, copies: 1, legalized: 0 },
    { name: "Conocimiento de Embarque", originals: 1, copies: 0, legalized: 1 },
  ],
};

// Configuración de estilos simple
const styles = {
  colors: { primary: "#213a8f", secondary: "#dddddd", white: "#ffffff" },
  fonts: {
    small: "7px",
    normal: "10px",
    medium: "12px",
    large: "16px",
    xlarge: "18px",
  },
  spacing: { padding: "2px 3px", sectionPadding: "4px", pagePadding: "20px" },
};

const RenderSettlementDelivery = (props) => {
  const { procedure } = props;
  const { openDialog } = useDialog();
  const fieldnames = [
    "numeroDeCaja/estante",
    "lugar",
    "cantidadOriginales",
    "cantidadFotocopiasLegalizadas",
    "cantidadFotocopiasSimples",
    "documentosDelDespacho",
    "observacionesDelArchivo",
    "cantidadDeHojasTotales",
    "fechaDeArchivo",
    "documentosParaArchivoDeAduana",
    "documentosSeccionL",
    "codigoDeInterno"
  ];
  const params = new URLSearchParams();
  fieldnames.forEach(name => params.append('fieldnames', name));
  const { data, loading } = Request({
    urlApi: `/Procedure/${procedure.id || procedure.procedureId}/datavalues?${params.toString()}`,
  });
  const { data: dataDocs, loading: loadingDocs } = Request({
    urlApi: `/Files/proc/${procedure.id || procedure.procedureId}/docs`,
  });

  const handleProform = (values) => {
    const urlEdit = `/ProcessStep/${procedure.id || procedure.procedureId}/byfields/value`;
    requestAuth("patch", urlEdit, values)
      .then((response) => {
        console.log("Datos editados correctamente en el servidor");
        openDialog(
          <LiquidacionProform {...props} accountNumber={values.account} dataDocs={dataDocs} />,
        );
      })
      .catch((error) => {
        console.error("Error al editar los registros:", error);
      });
  };

  if (!loading) {
    return (
      <div className="p-10 flex flex-col items-center">
        <Circle />
        <p className="mt-4 text-gray-500">Obteniendo datos previos...</p>
      </div>
    );
  }

  const accountOptions = MOCK_DATA.accountNumbers.map((item) => ({
    label: `${item.accountNumber} - ${item.name} ${item.fatherLastName}`,
    value: item.accountNumber,
  }));

  return (
    <FormDefault
      tittle="Formulario Planilla de Entrega"
      fields={[
        {
          label: "Cantidad de originales",
          name: "cantidadOriginales",
          typeInput: "number",
        },
        {
          label: "Cantidad de fotocopias simples",
          name: "cantidadFotocopiasSimples",
          typeInput: "number",
        },
        {
          label: "Cantidad de fotocopias legalizadas",
          name: "cantidadFotocopiasLegalizadas",
          typeInput: "number",
        },
        {
          label: "Observaciones",
          name: "observacionesDelArchivo",
          typeInput: "text",
        },
        {
          label: "Lugar",
          name: "lugar",
          typeInput: "text",
        },
      ]}
      initialValues={{
        account: "",
        cantidadOriginales: data?.cantidadOriginales,
        cantidadFotocopiasSimples: data?.cantidadFotocopiasSimples,
        cantidadFotocopiasLegalizadas: data?.cantidadFotocopiasLegalizadas,
        observacionesDelArchivo: data?.observacionesDelArchivo,
        lugar: data?.lugar,
      }}
      validationSchema={Yup.object().shape({})}
      onSubmit={handleProform}
      buttonName="Registrar"
    />
  );
};

const LiquidacionProform = (props) => {
  const { procedure, accountNumber, isQuotation, dataDocs } = props;
  const { user } = useAuth?.() || {
    user: { user: { name: "Usuario", fatherLastName: "", motherLastName: "" } },
  };
  const fieldnames = [
    "numeroDeCaja/estante",
    "lugar",
    "cantidadOriginales",
    "cantidadFotocopiasLegalizadas",
    "cantidadFotocopiasSimples",
    "documentosDelDespacho",
    "observacionesDelArchivo",
    "cantidadDeHojasTotales",
    "fechaDeArchivo",
    "documentosParaArchivoDeAduana",
    "documentosSeccionL",
    "codigoDeInterno"
  ];
  const params = new URLSearchParams();
  fieldnames.forEach(name => params.append('fieldnames', name));
  const { data: backendData, loading } = Request({
    urlApi: `/Procedure/${procedure.id || procedure.procedureId}/datavalues?${params.toString()}`,
  });

  if (!loading) {
    return (
      <div className="p-10 flex flex-col items-center">
        <Circle />
        <p className="mt-4 text-gray-500">Obteniendo datos del servidor...</p>
      </div>
    );
  }

  const data = backendData || {};
  const documentItems = MOCK_DATA.documentItems;

  const MyDoc = (
    <Document>
      <Page size="A4" style={{ padding: styles.spacing.pagePadding }}>
        {/* Marca de agua */}
        <Image
          src={
            // "https://res.cloudinary.com/dnwvlkw3r/image/upload/v1763749558/TRAZO/LOGOS/Imagen1_dwsaix.png"
            user?.institutionParam?.find(d => d.code === 'logo')?.value ?? ""
          }
          style={{
            position: "absolute",
            top: "25%",
            left: "25%",
            width: "400px",
            height: "400px",
            opacity: 0.1,
            transform: "translate(-50%, -50%)",
            zIndex: 0,
          }}
        />

        <View flexDirection="column" borderContent>
          <View
            padding={styles.spacing.sectionPadding}
            style={{ backgroundColor: styles.colors.primary }}
          ></View>

          <View flexDirection="column">
            {/* Header */}
            <View
              justifyContent="space-between"
              alignItems="center"
              padding={styles.spacing.sectionPadding}
            >
              <View width="25%">
                <Image
                  src={
                    // "https://res.cloudinary.com/dnwvlkw3r/image/upload/v1763749558/TRAZO/LOGOS/Imagen1_dwsaix.png"
                    user?.institutionParam?.find(d => d.code === 'logo')?.value ?? ""
                  }
                  style={{ height: "50px" }}
                />
              </View>
              <View width="60%" flexDirection="column" alignItems="flex-end">
                <Text
                  color={styles.colors.primary}
                  padding={styles.spacing.padding}
                  textAlign="end"
                  fontSize={styles.fonts.large}
                  bold
                >
                  {isQuotation ? "COTIZACIÓN" : "PLANILLA DE ENTREGA"}
                </Text>
                <Text
                  color={styles.colors.primary}
                  padding={styles.spacing.padding}
                  fontSize={styles.fonts.xlarge}
                  bold
                  textAlign="end"
                >
                  {data.codigoDeInterno}
                </Text>
                <Text
                  fontSize={styles.fonts.normal}
                  padding={styles.spacing.padding}
                  textAlign="end"
                >
                  Oruro {dayjs().format("DD")} de {dayjs().format("MMMM")} del{" "}
                  {dayjs().format("YYYY")}
                </Text>
              </View>
            </View>

            {/* Datos básicos */}
            <View border={{ top: true }} padding="3px 10px">
              <Text padding={styles.spacing.padding}>
                <Text bold>Señor(es): </Text>
                {data.company.razonSocial}
              </Text>
              <Text padding={styles.spacing.padding}>
                <Text bold>NIT: </Text>
                {data.company.nit}
              </Text>
            </View>

            {/* Totales */}
            <View
              flexDirection="column"
              padding="3px 10px"
              border={{ top: true, bottom: true }}
            >
              <Text>
                <Text bold>Lugar del Archivo: </Text>
                {data.lugar}
              </Text>
              <Text>
                <Text bold>Cantidad Originales: </Text>
                {data.cantidadOriginales}
              </Text>
              <Text>
                <Text bold>Cantidad Fotocopias Legalizadas: </Text>
                {data.cantidadFotocopiasLegalizadas}
              </Text>
              <Text>
                <Text bold>Cantidad Fotocopias Simples: </Text>
                {data.cantidadFotocopiasSimples}
              </Text>
            </View>

            <View padding="3px 10px">
              <Text bold>
                Observaciones: {data.observacionesDelArchivo}
              </Text>
            </View>

            <View border={{ top: true }} padding="3px 10px">
              <Text
                // fontSize={styles.fonts.medium}
                // bold
                textAlign="left"
                // color={styles.colors.primary}
                // style={{ marginBottom: "5px" }}
                bold
              >
                LISTA DE DOCUMENTOS
              </Text>
            </View>
            <View
              padding="3px 10px"
              width="100%"
              flexDirection="column"
            >
              <View width="100%">
                <CustomTablePDF
                  fields={[
                    { label: "Tipo de Documento", name: "name", width: "55" },
                    { label: "Nro. Documento", name: "number", width: "25" },
                    { label: "Fecha", name: "emissionDate", width: "10", type: "dateOnly" },
                    { label: "Detalle", name: "url", width: "10", type: "link" },
                  ]}
                  data={dataDocs ?? []}
                  border={{ top: true, left: true, bottom: true, right: true }}
                />
              </View>
            </View>
            {/* {data.subdatas && data.subdatas.length > 0 && (
            )} */}

            {/* Sección de Firmas */}
            <View
              style={{ marginTop: "150px", marginBottom: "40px" }}
              flexDirection="row"
              justifyContent="space-around"
            >
              {/* Firma Izquierda */}
              <View width="35%" alignItems="center" flexDirection="column">
                <View
                  border={{ top: true }}
                  width="100%"
                  style={{ marginBottom: "5px" }}
                />
                <Text bold fontSize={styles.fonts.normal}>
                  ENTREGUÉ CONFORME
                </Text>
                <Text fontSize={styles.fonts.small}>(Firma)</Text>
              </View>

              {/* Firma Derecha */}
              <View width="35%" alignItems="center" flexDirection="column">
                <View
                  border={{ top: true }}
                  width="100%"
                  style={{ marginBottom: "5px" }}
                />
                <Text bold fontSize={styles.fonts.normal}>
                  RECIBÍ CONFORME
                </Text>
                <Text fontSize={styles.fonts.small}>(Firma)</Text>
              </View>
            </View>

            {/* Footer */}
            <View style={{ marginTop: "20px" }} flexDirection="column">
              <View flexDirection="column" padding="3px 10px">
                <View width="50%">
                  <Text padding={styles.spacing.padding}>JAVEST S.R.L</Text>
                  <Text padding={styles.spacing.padding}>NIT: 516076028</Text>
                </View>
                <View width="50%">
                  <Text padding={styles.spacing.padding}>
                    Elaborado por: {user?.user?.name || "Usuario"}{" "}
                    {user?.user?.fatherLastName || ""}
                  </Text>
                </View>
              </View>

              <View
                flexDirection="column"
                padding="4px 0"
                justifyContent="center"
                alignItems="center"
                style={{ backgroundColor: styles.colors.primary }}
              >
                <Text
                  padding={styles.spacing.padding}
                  color={styles.colors.white}
                >
                  Dirección Montecinos esquina Velazco Galvarro, Nro 991 -
                  Oruro, Bolivia
                </Text>
                <Text
                  padding={styles.spacing.padding}
                  color={styles.colors.white}
                >
                  Tel: (591)76426163 - 2119416
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );

  return (
    <div>
      <PDFViewer style={{ width: 900, height: window.innerHeight - 100 }}>
        {MyDoc}
      </PDFViewer>
    </div>
  );
};

export default RenderSettlementDelivery;
